// General
import React, { useContext } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';
import ProtectedRoute from './authentication/ProtectedRoute';
import AuthenticatedLayout from './authentication/AuthenticatedLayout';
import { FIELD_STAFF_HOME, FIELD_STAFF_ROLE } from './authentication/roles';

// Authentication
import LoginPage from './pages/authentication/login/LoginPage';
import RegisterPage from './pages/authentication/register/RegisterPage';
import ResetPasswordPage from './pages/authentication/reset-password/ResetPasswordPage';
import RespondPage from './pages/respond/RespondPage';

// Allocate
import EquipmentAllocationTable from './pages/allocate/loaders/EquipmentAllocationTable';
import WalkwayCrewAllocationPage from './pages/allocate/walkway-crew/WalkwayCrewAllocationPage';
import SaltingRoutesAllocationPage from './pages/allocate/salting-route/SaltingRoutesAllocationPage';
import MapView from './pages/allocate/map-view/MapView';
import PlowAllocationPage from './pages/allocate/plow/PlowAllocationPage';
import StaffAllocationPage from './pages/allocate/staff/StaffAllocationPage';
import AllocationOverviewPage from './pages/allocate/overview/AllocationOverview';

// Dispatch
import WalkwayCrewDispatchPage from './pages/dispatch/walkway-crews/WalkwayCrewDispatch';
import SaltingRoutesDispatchPage from './pages/dispatch/salting-routes/SaltingRoutesDispatch';
import LoaderAllocationDispatchPage from './pages/dispatch/loaders/LoaderAllocationDispatch';
import DispatchHistoryPage from './pages/dispatch/history/DispatchHistory';
import MessageListView from './pages/dispatch/messages/MessageListView';

// Debrief
import DebriefJobs from './pages/debrief/jobs/DebriefJobs';
import ExploreJobs from './pages/debrief/explore-jobs/ExploreJobs';
import JobsSummary from './pages/debrief/jobs-summary/JobsSummary';
import HistoricalWeather from './pages/debrief/historical-weather/HistoricalWeather';
import StaffAssessments from './pages/debrief/staff-assessments/StaffAssessments';

// Estimate
import EstimatePage from './pages/estimate/EstimatePage';

// Project
import ProjectionPage from './pages/project/ProjectionPage';

// Requests
import RequestsPage from './pages/requests/RequestsPage';

// Reports
import ReportsCentre from './pages/reports/ReportsCentre';

// Databases
import ClientsListView from './pages/databases/clients/ClientsListView';
import SitesListView from './pages/databases/sites/SitesListView';
import EquipmentListView from './pages/databases/vehicles/EquipmentListView';
import EquipmentsPage from './pages/databases/equipments/EquipmentsPage';
import StaffListView from './pages/databases/staff/StaffListView';
import VehicleSubtypeListView from './pages/databases/vehicle-subtypes/VehicleSubtypeListView';
import StormsListView from './pages/databases/storms/StormListView';

// Client Care
import StormStrategyPage from './pages/client-care/storm-strategy/StormStrategyPage';
import BulkStormStrategyPage from './pages/client-care/bulk-storm-strategy/BulkStormStrategyPage';
import StormStrategiesPage from './pages/client-care/strategy-history/StormStrategiesPage';
import StormStrategySurveys from './pages/client-care/survey-responses/StormStrategySurveys';

// Home
import HomePage from './pages/home/HomePage';

// Execute
import ExecutePage from './pages/ExecutePage/ExecutePage';

// User
import ProfilePage from './pages/user/profile/ProfilePage';
import NotificationListView from './pages/user/notifications/NotificationListView';

// Integration
import BambooSetting from './pages/integrations/bamboohr/BambooSetting';
import JotformSetting from './pages/integrations/jotform/JotformSetting';

// Mobile (field staff)
import FieldStaffApp from './pages/mobile/FieldStaffApp';

function AppContent() {
  const { userRole } = useContext(AuthContext);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/r/:token" element={<RespondPage />} />
      <Route
        path={`${FIELD_STAFF_HOME}/*`}
        element={
          <ProtectedRoute allowedRoles={[FIELD_STAFF_ROLE]} allowFieldStaff>
            <FieldStaffApp />
          </ProtectedRoute>
        }
      />
      <Route path="/"
        element={<AuthenticatedLayout />}
      >
        <Route
          index
          element={
            <ProtectedRoute>
              {userRole === 'client' ? <Navigate to="/requests" replace /> : <HomePage />}
            </ProtectedRoute>
          }
        />
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="allocation"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <EquipmentAllocationTable />
            </ProtectedRoute>
          }
        />
        <Route path="allocate/overview/:setupId" element={<ProtectedRoute allowedRoles={['admin', 'standard']}><AllocationOverviewPage /></ProtectedRoute>} />
        <Route
          path="sites"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <SitesListView />
            </ProtectedRoute>
          }
        />
        <Route
          path="map"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <MapView />
            </ProtectedRoute>
          }
        />
        <Route
          path="plow"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <PlowAllocationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="staffAllocation"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <StaffAllocationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="walkwayCrew"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <WalkwayCrewAllocationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="walkwayCrewDispatch"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <WalkwayCrewDispatchPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="SaltingRoute"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <SaltingRoutesAllocationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="SaltingRouteDispatch"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <SaltingRoutesDispatchPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="LoaderDispatch"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <LoaderAllocationDispatchPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="estimatepage"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <EstimatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="requests"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard', 'client']}>
              <RequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="vehicles"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <EquipmentListView />
            </ProtectedRoute>
          }
        />
        <Route
          path="equipments"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <EquipmentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="staff"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <StaffListView />
            </ProtectedRoute>
          }
        />
        <Route
          path="projection"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <ProjectionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="subtypes"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <VehicleSubtypeListView />
            </ProtectedRoute>
          }
        />
        <Route
          path="storms"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <StormsListView />
            </ProtectedRoute>
          }
        />
        <Route
          path="messages"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <MessageListView />
            </ProtectedRoute>
          }
        />
        <Route
          path="clients"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <ClientsListView />
            </ProtectedRoute>
          }
        />
        <Route
          path="dispatchHistory"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <DispatchHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="notifications"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <NotificationListView />
            </ProtectedRoute>
          }
        />
        <Route
          path="debriefJobs"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <DebriefJobs />
            </ProtectedRoute>
          }
        />
        <Route
          path="exploreJobs"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <ExploreJobs />
            </ProtectedRoute>
          }
        />
        <Route
          path="jobsSummary"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <JobsSummary />
            </ProtectedRoute>
          }
        />
        <Route
          path="historical-weather"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <HistoricalWeather />
            </ProtectedRoute>
          }
        />
        <Route
          path="staff-assessments"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <StaffAssessments />
            </ProtectedRoute>
          }
        />
        <Route
          path="storm-strategy"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <StormStrategyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="bulk-storm-strategy"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <BulkStormStrategyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="storm-strategies"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <StormStrategiesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="storm-strategy-surveys"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <StormStrategySurveys />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <ReportsCentre />
            </ProtectedRoute>
          }
        />
        <Route
          path="bambooSetting"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <BambooSetting />
            </ProtectedRoute>
          }
        />
        <Route
          path="jotformSetting"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <JotformSetting />
            </ProtectedRoute>
          }
        />
        <Route
          path="execute"
          element={
            <ProtectedRoute allowedRoles={['admin', 'standard']}>
              <ExecutePage />
            </ProtectedRoute>
          }
        />
{/*         <Route
          path="client-dashboard"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientDashboard />
            </ProtectedRoute>
          }
        /> */}
      </Route>
    </Routes>
  );
}

export default AppContent;
